import React, { ReactElement, ReactNode } from 'react';
import { Tabs as AntTabs } from 'antd';
import styled, { css } from 'styled-components';

type TabsType = 'line' | 'card' | 'editable-card';
type TabsSize = 'large' | 'middle' | 'small';
type TabPosition = 'top' | 'right' | 'bottom' | 'left';

interface TabItem {
  key: string;
  label: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
  closable?: boolean;
  forceRender?: boolean;
  destroyOnHidden?: boolean;
  icon?: ReactNode;
}

interface TabsCustomStyles {
  itemFontFamily?: string;
  itemFontWeight?: number | string;
  itemFontSize?: CSSSize;
  itemLineHeight?: CSSSize;
  itemColor?: Color;
  itemActiveColor?: Color;
  itemGutter?: CSSSize;
  inkBarColor?: Color;
  inkBarHeight?: CSSSize;
  hideNavBaseline?: boolean;
  navMarginBottom?: CSSSize | 0;
}

interface TabsProps {
  activeKey?: string;
  defaultActiveKey?: string;
  type?: TabsType;
  size?: TabsSize;
  tabPosition?: TabPosition;
  centered?: boolean;
  items?: TabItem[];
  onChange?: (activeKey: string) => void;
  onTabClick?: (key: string, event: React.MouseEvent | React.KeyboardEvent) => void;
  className?: string;
  customStyle?: CustomStyle;
  customStyles?: TabsCustomStyles;
}

interface CustomStyle {
  wrapper?: {
    backgroundColor?: string;
    borderRadius?: CSSSize;
    padding?: CSSSize;
  };
  tabs?: {
    activ?: {
      backgroundColor?: string;
      color?: string;
    };
    color?: string;
    margin?: string;
    padding?: string;
    borderRadius?: CSSSize;
    transition?: string;
  };
  nav?: {
    borderBottom: string;
  };
  displayTabsInkBar?: string;
  marginTop?: CSSSize;
  paddingRight?: CSSSize;
  marginRight?: CSSSize;
}

function Tabs({
  activeKey,
  defaultActiveKey,
  type = 'line',
  size = 'middle',
  tabPosition = 'top',
  centered,
  items,
  onChange,
  onTabClick,
  className,
  customStyle,
  customStyles,
}: TabsProps): ReactElement {
  return (
    <StyledTabs
      activeKey={activeKey}
      defaultActiveKey={defaultActiveKey}
      type={type}
      size={size}
      tabPosition={tabPosition}
      centered={centered}
      items={items}
      onChange={onChange}
      onTabClick={onTabClick}
      className={className}
      customstyle={customStyle}
      customstyles={customStyles}
    />
  );
}

const StyledTabs = styled(AntTabs).withConfig({
  shouldForwardProp: prop =>
    (prop as string) !== 'customstyle' && (prop as string) !== 'customstyles',
})<{
  customstyle?: CustomStyle;
  customstyles?: TabsCustomStyles;
}>`
  .ant-tabs-nav {
    &::before {
      border-bottom: ${props => props.customstyle?.nav?.borderBottom};
    }
  }

  .ant-tabs-nav-wrap {
    background-color: ${props => props.customstyle?.wrapper?.backgroundColor};
    border-radius: ${props => props.customstyle?.wrapper?.borderRadius};
    padding: ${props => props.customstyle?.wrapper?.padding};
  }

  .ant-tabs-tab {
    margin: ${props => props.customstyle?.tabs?.margin} !important;
    padding: ${props => props.customstyle?.tabs?.padding};
    border-radius: ${props => props.customstyle?.tabs?.borderRadius};
    transition: ${props => props.customstyle?.tabs?.transition};

    &.ant-tabs-tab-active {
      background-color: ${props => props.customstyle?.tabs?.activ?.backgroundColor};

      .ant-tabs-tab-btn {
        color: ${props => props.customstyle?.tabs?.activ?.color};
      }
    }

    .ant-tabs-tab-btn {
      color: ${props => props.customstyle?.tabs?.color};
    }
  }

  .ant-tabs-ink-bar {
    display: ${props => props.customstyle?.displayTabsInkBar};
  }

  ${({ customstyles }) =>
    customstyles &&
    css`
      .ant-tabs-nav {
        margin-bottom: ${customstyles.navMarginBottom};
      }

      ${customstyles.hideNavBaseline &&
      css`
        .ant-tabs-nav::before {
          border-bottom: none;
        }
      `}

      .ant-tabs-tab {
        padding: 0;
      }

      .ant-tabs-tab + .ant-tabs-tab {
        margin-left: ${customstyles.itemGutter};
      }

      .ant-tabs-tab .ant-tabs-tab-btn {
        font-family: ${customstyles.itemFontFamily};
        font-weight: ${customstyles.itemFontWeight};
        font-size: ${customstyles.itemFontSize};
        line-height: ${customstyles.itemLineHeight};
        color: ${customstyles.itemColor};
      }

      .ant-tabs-tab:hover .ant-tabs-tab-btn,
      .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
        color: ${customstyles.itemActiveColor};
      }

      .ant-tabs-ink-bar {
        height: ${customstyles.inkBarHeight};
        background: ${customstyles.inkBarColor};
      }
    `}
`;

export { Tabs };
